import type { Profile } from "@spork/s4p-server-sdk";
import type { ReactNode } from "react";

import { i18n } from "@/i18n";

import { getProfileFieldDescription } from "../config/profileFieldDescriptions";
import type { ProfileFormFieldDefinition } from "../config/profileFormGroups";
import { ProfileFieldInput } from "../fields/ProfileFieldInput";
import { ReadonlySummaryProfileField } from "../fields/ReadonlySummaryProfileField";
import { ProfileField } from "../types/profileFieldInput";
import type { ProfileFormFieldHandlers } from "../types/profileFormFieldHandlers";
import { isStandardFieldReadOnly } from "./isStandardFieldReadOnly";

export function renderProfileFormField(
    person: Profile,
    field: ProfileFormFieldDefinition,
    handlers: ProfileFormFieldHandlers,
): ReactNode {
    if (field.type === "standard") {
        const fieldData: ProfileField = {
            field_code: field.key,
            field_name: i18n.t(`profile.fields.${field.key}`),
            field_type: "string",
            value: handlers.values[field.key],
            text: handlers.values[field.key],
            read_only:
                handlers.readOnly || isStandardFieldReadOnly(person, field.key),
        };
        return (
            <ProfileFieldInput
                pipedriveId={person.id}
                field={fieldData}
                value={handlers.values[field.key]}
                onChange={(nextValue) =>
                    handlers.onUpdateStandardField(field.key, nextValue)
                }
            />
        );
    }

    if (field.type === "readonly_summary") {
        const label = i18n.t(`profile.fields.${field.labelKey}`);
        return (
            <ReadonlySummaryProfileField
                key={field.labelKey}
                person={person}
                label={label}
                fieldCodes={field.fieldCodes}
            />
        );
    }

    const customField = person.custom_fields[field.fieldCode];
    if (!customField) {
        return null;
    }
    const fieldWithDescription = {
        ...person.custom_fields[field.fieldCode],
        description: getProfileFieldDescription(field.fieldCode),
    };

    return (
        <ProfileFieldInput
            pipedriveId={person.id}
            field={fieldWithDescription}
            value={handlers.values.custom_fields[field.fieldCode]}
            readOnly={handlers.readOnly}
            onChange={(nextValue) =>
                handlers.onUpdateCustomField(field.fieldCode, nextValue)
            }
        />
    );
}
